/** Plain helpers for showing devices and readings. No React here, so they're easy to test. */
import type { Device, DeviceEvent, PortConfig, Reading, CommandRecord } from "./api.ts";

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

export type Status = "online" | "late" | "offline" | "never";

/**
 * FMS boards send a heartbeat every few minutes and Milesight sensors report every 10-20
 * minutes, so 30 minutes of silence means a missed report and 2 hours means it's down.
 */
export const LATE_AFTER = 30 * MIN;
export const OFFLINE_AFTER = 2 * HOUR;

export function deviceStatus(d: Pick<Device, "lastSeenAt">, now: number): Status {
  if (d.lastSeenAt === undefined) return "never";
  const silent = now - d.lastSeenAt;
  if (silent < LATE_AFTER) return "online";
  if (silent < OFFLINE_AFTER) return "late";
  return "offline";
}

export const STATUS_TEXT: Record<Status, string> = {
  online: "Online",
  late: "Report missed",
  offline: "Offline",
  never: "Never heard",
};

export const STATUS_TONE: Record<Status, "good" | "warn" | "crit" | "muted"> = {
  online: "good",
  late: "warn",
  offline: "crit",
  never: "muted",
};

export function ago(at: number | undefined, now: number): string {
  if (at === undefined) return "never";
  const s = Math.max(0, now - at) / 1000;
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} h ago`;
  const days = Math.round(s / 86_400);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

export function clock(at: number, now: number): string {
  const d = new Date(at);
  const time = d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
  if (now - at < DAY && new Date(now).getDate() === d.getDate()) return time;
  return `${d.toLocaleDateString("en-AU", { day: "numeric", month: "short" })}, ${time}`;
}

export { isDeviceHealth, isFmsBoard, isOnOff, portKey, portLabel, readsDistance, tankLevel, tankPercent, tankRange } from "../../../packages/core/src/ports.ts";
import { isDeviceHealth, isOnOff, portKey, portLabel, tankLevel } from "../../../packages/core/src/ports.ts";

export function formatNumber(value: number): string {
  const abs = Math.abs(value);
  const dp = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return value.toLocaleString("en-AU", { maximumFractionDigits: dp, minimumFractionDigits: 0 });
}

export function formatReading(device: Device, r: Pick<Reading, "port" | "value" | "unit">): { value: string; unit: string } {
  if (isOnOff(device, r.port)) return { value: r.value ? "On" : "Off", unit: "" };
  if (r.port === "wind_direction") return { value: `${compass(r.value)} ${Math.round(r.value)}`, unit: "°" };
  // Ultrasonic tank sensors report the gap above the water; people want the water level.
  const cfg = device.ports.find((p) => portKey(p) === r.port);
  return { value: formatNumber(tankLevel(cfg, r.value)), unit: r.unit ?? "" };
}

export function compass(deg: number): string {
  const names = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
  return names[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16]!;
}

export interface ShownReading extends Reading {
  label: string;
  onOff: boolean;
}

/**
 * Newest readings split into what the device measures and how the device itself is doing,
 * in the order the ports were set up (so the most important ones lead). Ports switched off
 * in set-up are hidden.
 */
export function arrangeReadings(device: Device, readings: Reading[]): { measured: ShownReading[]; health: ShownReading[] } {
  const order = new Map(device.ports.map((p, i) => [portKey(p), i]));
  const disabled = new Set(device.ports.filter((p) => !p.enabled).map(portKey));
  const shown = readings
    .filter((r) => !disabled.has(r.port))
    .map((r) => ({ ...r, label: portLabel(device, r.port), onOff: isOnOff(device, r.port) }))
    .sort((a, b) => (order.get(a.port) ?? 999) - (order.get(b.port) ?? 999) || a.port.localeCompare(b.port));
  return { measured: shown.filter((r) => !isDeviceHealth(r.port)), health: shown.filter((r) => isDeviceHealth(r.port)) };
}

/** 1-4 bars from LoRa RSSI. */
export function signalBars(rssi: number | undefined): number {
  if (rssi === undefined) return 0;
  return rssi > -85 ? 4 : rssi > -95 ? 3 : rssi > -105 ? 2 : 1;
}

const MODEL_NAMES: Record<string, string> = {
  multipro: "FMS MultiPro",
  mini: "FMS Mini",
  uc300: "Milesight UC300",
  uc501: "Milesight UC501",
  uc511: "Milesight UC511",
  wts506: "Milesight WTS506",
  "qoto-02f05": "QOTO valve",
};

export function modelName(model: string): string {
  return MODEL_NAMES[model] ?? (model.startsWith("em") ? `Milesight ${model.toUpperCase()}` : model.toUpperCase());
}

export function formatEui(eui: string): string {
  return eui.toUpperCase().replace(/(.{4})(?=.)/g, "$1 ");
}

export const EVENT_INFO: Record<DeviceEvent["kind"], { label: string; tone: "good" | "warn" | "crit" | "info" }> = {
  online: { label: "Online", tone: "good" },
  offline: { label: "Went offline", tone: "crit" },
  "handshake-failed": { label: "Couldn't connect", tone: "warn" },
  "power-lost": { label: "Lost power", tone: "crit" },
  restarted: { label: "Restarted", tone: "warn" },
  "timer-expired": { label: "Safety timer switched off", tone: "info" },
  "rule-executed": { label: "Safety rule switched off", tone: "warn" },
  "sensor-fault": { label: "Sensor fault", tone: "crit" },
  "unregistered-device": { label: "Unregistered device", tone: "info" },
  "unknown-message": { label: "Unknown message", tone: "info" },
  "discarded-data": { label: "Data discarded", tone: "info" },
  "decode-error": { label: "Couldn't read message", tone: "warn" },
  "modbus-error": { label: "RS485 / Modbus error", tone: "warn" },
  setup: { label: "Board setting", tone: "info" },
  threshold: { label: "Reading past its threshold", tone: "warn" },
  rule: { label: "Safety rule", tone: "info" },
};

export const COMMAND_INFO: Record<CommandRecord["status"], { label: string; tone: "good" | "warn" | "crit" | "info" | "muted" }> = {
  queued: { label: "Waiting to send", tone: "info" },
  sent: { label: "Sent, waiting for device", tone: "info" },
  confirmed: { label: "Done (device confirmed)", tone: "good" },
  unconfirmed: { label: "Sent (device can't confirm)", tone: "warn" },
  failed: { label: "Failed", tone: "crit" },
  cancelled: { label: "Cancelled", tone: "muted" },
};

export interface DeviceSummary {
  device: Device;
  status: Status;
}

export function countByStatus(devices: Device[], now: number): Record<Status, number> {
  const out: Record<Status, number> = { online: 0, late: 0, offline: 0, never: 0 };
  for (const d of devices) out[deviceStatus(d, now)]++;
  return out;
}

/**
 * Breaks a series into runs wherever the gap is far longer than usual, so a chart doesn't
 * draw a straight line across hours when the device was offline.
 */
export function splitAtGaps<T extends { at: number }>(points: T[], factor = 4): T[][] {
  if (points.length < 2) return points.length ? [points] : [];
  const gaps = points.slice(1).map((p, i) => p.at - points[i]!.at).sort((a, b) => a - b);
  const typical = gaps[Math.floor(gaps.length / 2)]!;
  const limit = Math.max(typical * factor, 5 * MIN);
  const runs: T[][] = [[points[0]!]];
  for (let i = 1; i < points.length; i++) {
    if (points[i]!.at - points[i - 1]!.at > limit) runs.push([]);
    runs.at(-1)!.push(points[i]!);
  }
  return runs;
}

/** Round chart axis ticks: 0, 250, 500 ... */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (min === max) {
    const pad = Math.abs(min) * 0.1 || 1;
    min -= pad;
    max += pad;
  }
  const raw = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 2.5, 5, 10].find((s) => s * mag >= raw) ?? 10) * mag;
  const ticks: number[] = [];
  const end = Math.ceil(max / step - 1e-9) * step;
  for (let v = Math.floor(min / step + 1e-9) * step; v <= end + step * 1e-6; v += step) ticks.push(Number(v.toFixed(10)));
  return ticks;
}

/**
 * One number for "how's the farm": share of devices reporting, less a little for each open
 * alert. Only a summary; the alerts list says what to do.
 */
export function healthScore(devices: Pick<Device, "lastSeenAt">[], alerts: Array<{ severity: string; acked?: unknown; ongoing?: boolean }>, now: number): number {
  if (!devices.length) return 0;
  const c = countByStatus(devices as Device[], now);
  // An acknowledged problem that's still going counts half: someone's onto it, but it isn't fixed.
  const weight = (a: { acked?: unknown; ongoing?: boolean }) => (!a.acked ? 1 : a.ongoing ? 0.5 : 0);
  const base = (100 * (c.online + c.late * 0.5)) / devices.length;
  const penalty = alerts.reduce((n, a) => n + (a.severity === "crit" ? 4 : a.severity === "warn" ? 2 : 0) * weight(a), 0);
  return Math.max(0, Math.min(100, Math.round(base - penalty)));
}
